import { useMemo, useState } from "react";
import { APP_CURRENCY, APP_LOCALE } from "../../../config/locale";
import type {
  WorkItemStatus,
  WorkSubcontractorItem,
} from "../../../obras/obrasApi";
import type { Subcontractor } from "../../../subempreiteiros/subempreiteirosApi";
import type { Supplier } from "../../../fornecedores/fornecedoresApi";
import { WorkMapExportButtons } from "./WorkMapExportButtons";
import { NovaLinhaSubempreitadaModal } from "./NovaLinhaSubempreitadaModal";
import { EntidadeExecutanteField } from "./EntidadeExecutanteField";
import { ListToolbar } from "../../../components/ListToolbar";
import {
  exportWorkSubcontractorsToExcel,
  exportWorkSubcontractorsToPdf,
  imputeWorkSubcontractorCost,
  removeWorkSubcontractorCostImputation,
} from "../../../obras/obrasApi";

interface WorkSubcontractorsTabProps {
  workId: number;
  items: WorkSubcontractorItem[];
  subcontractors: Subcontractor[];
  // Fornecedores entram nas sugestões a par dos sub-empreiteiros: são duas tabelas distintas, mas
  // quem executa a obra é registado ora numa ora noutra, e este campo é texto livre. Sugerir só
  // uma delas deixava de fora metade das entidades já criadas — e o auto, que herda o nome desta
  // linha, ficava sem forma de lhes chegar.
  suppliers: Supplier[];
  itemStatuses: WorkItemStatus[];
  onItemChange: (
    itemId: number,
    updates: Partial<WorkSubcontractorItem>,
  ) => void;
  onItemReplaced: (updatedItem: WorkSubcontractorItem) => void;
  onItemCreated: (createdItem: WorkSubcontractorItem) => void;
  onCostImputed?: () => void;
}

const normalizeNumberInput = (value: string): string => {
  const cleaned = value.trim().replace(/\s/g, "");
  if (!cleaned) return "";

  const hasComma = cleaned.includes(",");
  const hasDot = cleaned.includes(".");

  if (hasComma && hasDot) {
    return cleaned.replace(/\./g, "").replace(",", ".");
  }

  if (hasComma) {
    return cleaned.replace(",", ".");
  }

  return cleaned;
};

const parseNumberInput = (value: string): number => {
  const normalized = normalizeNumberInput(value);
  if (!normalized) return 0;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatNumberInput = (value: number, decimals: number): string => {
  if (!Number.isFinite(value)) return "";
  return value.toLocaleString(APP_LOCALE, {
    useGrouping: false,
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
};

const formatCurrency = (value: number): string => {
  const formatted = new Intl.NumberFormat(APP_LOCALE, {
    style: "currency",
    currency: APP_CURRENCY,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

  return formatted.replace(/\./g, " ");
};

const formatDateInput = (value?: string | null): string => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

const normalizeDateValue = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed;
};

// Alerta simples [4.2.3]: ainda por pedir e a data de necessidade já passou.
const isNeedOverdue = (
  statusCode: string,
  needDate?: string | null,
): boolean => {
  if (statusCode !== "em_aberto" || !needDate) return false;
  const parsed = new Date(needDate);
  if (Number.isNaN(parsed.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return parsed < today;
};

export function WorkSubcontractorsTab({
  workId,
  items,
  subcontractors,
  suppliers,
  itemStatuses,
  onItemChange,
  onItemReplaced,
  onItemCreated,
  onCostImputed,
}: WorkSubcontractorsTabProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [filter, setFilter] = useState("");
  const [numericDrafts, setNumericDrafts] = useState<
    Record<string, string>
  >({});
  const [imputingItemId, setImputingItemId] = useState<number | null>(null);
  const [imputeErrors, setImputeErrors] = useState<
    Record<number, string>
  >({});

  const setImputeError = (itemId: number, message: string | null) => {
    setImputeErrors((prev) => {
      const next = { ...prev };
      if (message) {
        next[itemId] = message;
      } else {
        delete next[itemId];
      }
      return next;
    });
  };

  const handleImputeCost = async (itemId: number) => {
    if (imputingItemId !== null) return;
    setImputingItemId(itemId);
    setImputeError(itemId, null);
    try {
      const updated = await imputeWorkSubcontractorCost(workId, itemId);
      onItemReplaced(updated);
      onCostImputed?.();
    } catch (error) {
      setImputeError(
        itemId,
        error instanceof Error && error.message
          ? error.message
          : "Não foi possível imputar o custo.",
      );
    } finally {
      setImputingItemId(null);
    }
  };

  const handleRemoveImputation = async (itemId: number) => {
    if (imputingItemId !== null) return;
    setImputingItemId(itemId);
    setImputeError(itemId, null);
    try {
      const updated = await removeWorkSubcontractorCostImputation(
        workId,
        itemId,
      );
      onItemReplaced(updated);
      onCostImputed?.();
    } catch (error) {
      setImputeError(
        itemId,
        error instanceof Error && error.message
          ? error.message
          : "Não foi possível anular a imputação.",
      );
    } finally {
      setImputingItemId(null);
    }
  };

  const subcontractorOptions = useMemo(() => {
    const values = new Set<string>();
    [...subcontractors, ...suppliers].forEach((entidade) => {
      const name = entidade.name?.trim();
      if (name) {
        values.add(name);
      }
    });
    // Set() antes de ordenar: a mesma empresa costuma estar nas duas tabelas, e apareceria duas
    // vezes na lista de sugestões.
    return Array.from(values).sort((a, b) => a.localeCompare(b, "pt-PT"));
  }, [subcontractors, suppliers]);

  const typeOptions = useMemo(() => {
    const values = new Set<string>();
    items.forEach((item) => {
      if (item.type) {
        values.add(item.type);
      }
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const locationOptions = useMemo(() => {
    const values = new Set<string>();
    items.forEach((item) => {
      if (item.location) {
        values.add(item.location);
      }
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const getDraft = (key: string): string | undefined => numericDrafts[key];

  const setDraft = (key: string, value: string) => {
    setNumericDrafts((prev) => ({ ...prev, [key]: value }));
  };

  const clearDraft = (key: string) => {
    setNumericDrafts((prev) => {
      if (!(key in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleStatusChange = (itemId: number, value: string) => {
    const status = itemStatuses.find(
      (entry) => entry.id === Number(value),
    );
    if (!status) return;
    onItemChange(itemId, {
      workItemStatusId: status.id,
      statusCode: status.code,
      statusName: status.name,
    });
  };

  const shown = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) => {
      const haystack = [
        item.code,
        item.type,
        item.location,
        item.description,
        item.subcontractor,
        item.statusName,
      ]
        .filter((value): value is string => Boolean(value))
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [items, filter]);

  const totalValue = items.reduce(
    (sum, item) => sum + item.value,
    0,
  );

  const shownTotal = shown.reduce((sum, item) => sum + item.value, 0);

  return (
    <>
      <ListToolbar
        filter={filter}
        onFilterChange={setFilter}
        placeholder="Filtrar…"
        summary={
          <span className="text-secondary-text text-sm">
            Total {formatCurrency(shownTotal)} · {shown.length}{" "}
            {shown.length === 1 ? "item" : "itens"}
          </span>
        }
      >
        <button
          type="button"
          onClick={() => setIsCreating(true)}
          className="px-3 py-1.5 bg-primary text-primary-foreground text-sm"
          style={{ borderRadius: "var(--radius-button)" }}
        >
          Nova linha
        </button>
        <WorkMapExportButtons
          onExportExcel={() => exportWorkSubcontractorsToExcel(workId)}
          onExportPdf={() => exportWorkSubcontractorsToPdf(workId)}
        />
      </ListToolbar>

      {isCreating && (
        <NovaLinhaSubempreitadaModal
          workId={workId}
          subcontractors={subcontractors}
          suppliers={suppliers}
          onClose={() => setIsCreating(false)}
          onCreated={onItemCreated}
        />
      )}
      <div
        className="bg-card border border-border overflow-x-auto"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <table className="w-full" style={{ minWidth: 1600 }}>
        <thead>
          <tr className="border-b border-border bg-muted text-left">
            <th className="px-4 py-3">Código</th>
            <th className="px-4 py-3">Área</th>
            <th className="px-4 py-3">Capítulo</th>
            <th className="px-4 py-3">Descrição</th>
            <th className="px-4 py-3">Sub-empreiteiro</th>
            <th className="px-4 py-3">Estado</th>
            <th className="px-4 py-3">Necessidade</th>
            <th className="px-4 py-3 text-right">Valor</th>
            <th className="px-4 py-3">Imputação</th>
          </tr>
        </thead>
        <tbody>
          {filter !== "" && shown.length === 0 && (
            <tr>
              <td
                className="px-4 py-3 text-secondary-text text-sm"
                colSpan={9}
              >
                Sem resultados para o filtro.
              </td>
            </tr>
          )}
          {shown.map((item) => {
            const valueKey = `${item.id}-value`;
            const valueDraft =
              getDraft(valueKey) ?? formatNumberInput(item.value, 2);
            const overdue = isNeedOverdue(item.statusCode, item.needDate);

            return (
              <tr
                key={item.id}
                className="border-b border-border last:border-b-0"
              >
                <td className="px-4 py-3 align-top">
                  <input
                    type="text"
                    value={item.code}
                    onChange={(event) =>
                      onItemChange(item.id, {
                        code: event.target.value,
                      })
                    }
                    className="w-24 px-2 py-1 border border-border rounded bg-background text-foreground"
                    style={{ borderRadius: "var(--radius)" }}
                  />
                </td>
                <td className="px-4 py-3 align-top">
                  <input
                    type="text"
                    list="work-subcontractor-type-options"
                    value={item.type ?? ""}
                    onChange={(event) =>
                      onItemChange(item.id, {
                        type: event.target.value,
                      })
                    }
                    className="w-32 px-2 py-1 border border-border rounded bg-background text-foreground"
                    style={{ borderRadius: "var(--radius)" }}
                  />
                </td>
                <td className="px-4 py-3 align-top">
                  <input
                    type="text"
                    list="work-subcontractor-location-options"
                    value={item.location ?? ""}
                    onChange={(event) =>
                      onItemChange(item.id, {
                        location: event.target.value,
                      })
                    }
                    className="w-48 px-2 py-1 border border-border rounded bg-background text-foreground"
                    style={{ borderRadius: "var(--radius)" }}
                  />
                </td>
                <td className="px-4 py-3 align-top">
                  <textarea
                    rows={2}
                    value={item.description}
                    onChange={(event) =>
                      onItemChange(item.id, {
                        description: event.target.value,
                      })
                    }
                    className="w-full px-2 py-1 border border-border rounded bg-background text-foreground resize-y"
                    style={{ borderRadius: "var(--radius)", width: 340 }}
                  />
                </td>
                <td className="px-4 py-3 align-top">
                  <EntidadeExecutanteField
                    value={item.subcontractor}
                    onChange={(valor) =>
                      onItemChange(item.id, { subcontractor: valor })
                    }
                    options={subcontractorOptions}
                    inputClassName="w-48 px-2 py-1 border border-border rounded bg-background text-foreground"
                    panelPosition="fixed"
                    ariaLabel="Entidade executante"
                  />
                </td>
                <td className="px-4 py-3 align-top">
                  <select
                    value={item.workItemStatusId || ""}
                    onChange={(event) =>
                      handleStatusChange(item.id, event.target.value)
                    }
                    className="w-48 px-2 py-1 border border-border rounded bg-background text-foreground"
                    style={{ borderRadius: "var(--radius)" }}
                  >
                    {itemStatuses.map((status) => (
                      <option key={status.id} value={status.id}>
                        {status.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3 align-top">
                  <input
                    type="date"
                    value={formatDateInput(item.needDate)}
                    onChange={(event) =>
                      onItemChange(item.id, {
                        needDate: normalizeDateValue(event.target.value),
                      })
                    }
                    className="w-48 px-2 py-1 border border-border rounded bg-background text-foreground"
                    style={{ borderRadius: "var(--radius)" }}
                  />
                  {overdue && (
                    <div className="text-destructive text-xs mt-1">
                      Devia ter sido pedido
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 align-top text-right">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={valueDraft}
                    onChange={(event) => {
                      const value = event.target.value;
                      setDraft(valueKey, value);
                      const normalized = normalizeNumberInput(value);
                      if (!normalized) {
                        onItemChange(item.id, { value: 0 });
                        return;
                      }
                      onItemChange(item.id, {
                        value: parseNumberInput(value),
                      });
                    }}
                    onBlur={() => clearDraft(valueKey)}
                    className="w-32 px-2 py-1 border border-border rounded bg-background text-foreground text-right"
                    style={{ borderRadius: "var(--radius)" }}
                  />
                </td>
                <td className="px-4 py-3 align-top" style={{ minWidth: 180 }}>
                  {item.imputedCostId != null ? (
                    <div className="flex flex-col gap-1" style={{ alignItems: "flex-start" }}>
                      <span
                        className="inline-flex items-center px-2 py-0.5 text-xs bg-primary/10 text-primary border border-primary/20"
                        style={{ borderRadius: "var(--radius)" }}
                      >
                        Imputado · {formatCurrency(item.imputedAmount ?? 0)}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveImputation(item.id)}
                        disabled={imputingItemId === item.id}
                        className="text-destructive underline text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {imputingItemId === item.id ? "A anular…" : "Anular"}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleImputeCost(item.id)}
                      disabled={imputingItemId === item.id}
                      className="text-primary underline text-xs disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {imputingItemId === item.id
                        ? "A imputar…"
                        : "Imputar custo"}
                    </button>
                  )}
                  {imputeErrors[item.id] && (
                    <div className="text-destructive text-xs mt-1">
                      {imputeErrors[item.id]}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t border-border bg-muted">
            <td className="px-4 py-3" colSpan={7}>
              Total Sub-empreiteiros
            </td>
            <td className="px-4 py-3 text-right">
              {formatCurrency(totalValue)}
            </td>
            <td className="px-4 py-3" />
          </tr>
        </tfoot>
      </table>

      <datalist id="work-subcontractor-type-options">
        {typeOptions.map((value) => (
          <option key={value} value={value} />
        ))}
      </datalist>
      <datalist id="work-subcontractor-location-options">
        {locationOptions.map((value) => (
          <option key={value} value={value} />
        ))}
      </datalist>
      </div>
    </>
  );
}
